import { generateFieldChips } from 'app/components/AmWorkDesk/PlacementsDesk/filterHelper';
import moment from 'moment-timezone';
import _isArray from 'lodash/isArray';
import _ from 'lodash';
import { initialUnitAggregateSearch } from 'app/ComponentLibrary/Filter/UnitSearch/Index';
import { initialCandidateAggregateSearch } from 'app/ComponentLibrary/Filter/CandidateSearch';

export enum TaskSearchFilterName {
  candidateAggregate = 'candidateFilter',
  taskName = 'taskName',
  taskCategory = 'taskCategories',
  placementId = 'placementIds',
  candidateId = 'candidateIds',
  facilityId = 'facilityIds',
  orderId = 'orderIds',
  status = 'taskStatusIds',
  facility = 'facility',
  unit = 'unit',
  dueDate = 'dueDate',
  sentDateMin = 'sentDateMin',
  sentDateMax = 'sentDateMax',
  followUpDateMin = 'followUpDateMin',
  followUpDateMax = 'followUpDateMax',
  assignedTo = 'assignedToIds',
  createdBy = 'createdByIds',
  recipients = 'recipientIds',
}

export const defaultTaskFilterValues = {
  [TaskSearchFilterName.candidateAggregate]: null,
  [TaskSearchFilterName.taskName]: null,
  [TaskSearchFilterName.taskCategory]: null,
  [TaskSearchFilterName.placementId]: null,
  [TaskSearchFilterName.candidateId]: null,
  [TaskSearchFilterName.facilityId]: null,
  [TaskSearchFilterName.orderId]: null,
  [TaskSearchFilterName.status]: null,
  [TaskSearchFilterName.facility]: null,
  [TaskSearchFilterName.unit]: null,
  [TaskSearchFilterName.dueDate]: null,
  [TaskSearchFilterName.sentDateMin]: null,
  [TaskSearchFilterName.sentDateMax]: null,
  [TaskSearchFilterName.followUpDateMin]: null,
  [TaskSearchFilterName.followUpDateMax]: null,
  [TaskSearchFilterName.assignedTo]: null,
  [TaskSearchFilterName.createdBy]: null,
  [TaskSearchFilterName.recipients]: null,
};

export const returnTaskFilterBody = (filterName, filter, data, remove = false) => {
  if (!data || !filter) return;
  const temp = { ...data };
  const formattedFilterData = _isArray(filter) ? filter.map(f => f.value) : filter;
  // organizes filter items
  switch (filterName) {
    case TaskSearchFilterName.candidateId:
      if (remove) {
        temp[TaskSearchFilterName.candidateId] =
          temp[TaskSearchFilterName.candidateId]?.filter(t => t !== formattedFilterData?.value) || [];
      } else {
        temp[TaskSearchFilterName.candidateId] = formattedFilterData;
      }
      break;
    case TaskSearchFilterName.candidateAggregate:
      if (remove) {
        // Candidate filter chips have structure: { key: 'selections', value: choice }
        // where choice.value is the actual candidate ID
        // formattedFilterData is chip.value which is { key: 'selections', value: choice }
        let candidateIdToRemove;
        if (formattedFilterData && typeof formattedFilterData === 'object' && 'value' in formattedFilterData) {
          // Handle chip structure: { key: 'selections', value: choice }
          // choice.value is the candidate ID, or choice.id or choice.candidateId
          candidateIdToRemove = formattedFilterData.value?.value || formattedFilterData.value?.id || formattedFilterData.value?.candidateId || formattedFilterData.value;
        } else {
          // Fallback: if it's already a primitive value
          candidateIdToRemove = formattedFilterData;
        }
        const currentCandidate = temp[TaskSearchFilterName.candidateAggregate];
        // On /search/tasks, candidateFilter is stored as aggregate object { selections: [...] }
        if (currentCandidate && typeof currentCandidate === 'object' && !_isArray(currentCandidate) && Array.isArray(currentCandidate.selections)) {
          const filtered = currentCandidate.selections.filter(
            (t: any) => (t?.value ?? t?.id ?? t?.candidateId) != candidateIdToRemove,
          );
          temp[TaskSearchFilterName.candidateAggregate] =
            filtered.length === 0
              ? { ...initialCandidateAggregateSearch }
              : { ...currentCandidate, selections: filtered };
          temp[TaskSearchFilterName.candidateId] = filtered.map((item: any) => item?.value ?? item?.id ?? item?.candidateId).filter(Boolean);
        } else {
          temp[TaskSearchFilterName.candidateId] =
            (temp[TaskSearchFilterName.candidateId] && Array.isArray(temp[TaskSearchFilterName.candidateId])
              ? temp[TaskSearchFilterName.candidateId].filter(t => t != candidateIdToRemove)
              : []) || [];
        }
      } else {
        const candidateValue = filter;
        const selectedCandidates = candidateValue?.selections
          ? candidateValue.selections.map(item => item.value || item.id || item.candidateId)
          : Array.isArray(candidateValue)
            ? candidateValue.map(item => item.value || item.id || item.candidateId)
            : [];
        temp[TaskSearchFilterName.candidateId] = selectedCandidates.filter(
          candidate => candidate !== undefined && candidate !== null,
        );
      }
      break;
    case TaskSearchFilterName.facilityId:
      if (remove) {
        temp[TaskSearchFilterName.facilityId] =
          temp[TaskSearchFilterName.facilityId]?.filter(t => t !== formattedFilterData?.value) || [];
      } else {
        temp[TaskSearchFilterName.facilityId] = formattedFilterData;
      }
      break;
    case TaskSearchFilterName.facility:
      if (remove) {
        temp[TaskSearchFilterName.facility] =
          temp[TaskSearchFilterName.facility]?.filter(t => t !== formattedFilterData?.value) || [];
      } else {
        temp[TaskSearchFilterName.facility] = formattedFilterData;
      }
      break;
    case TaskSearchFilterName.unit:
      if (remove) {
        // Unit filter chips have structure: { key: 'selections', value: choice }
        // where choice.value is the actual unit ID
        // formattedFilterData is chip.value which is { key: 'selections', value: choice }
        let unitIdToRemove;
        if (formattedFilterData && typeof formattedFilterData === 'object' && 'value' in formattedFilterData) {
          // Handle chip structure: { key: 'selections', value: choice }
          // choice.value is the unit ID, or choice.id or choice.unitId
          unitIdToRemove = formattedFilterData.value?.value || formattedFilterData.value?.id || formattedFilterData.value?.unitId || formattedFilterData.value;
        } else {
          // Fallback: if it's already a primitive value
          unitIdToRemove = formattedFilterData;
        }
        const currentUnit = temp[TaskSearchFilterName.unit];
        // On /search/tasks, unit is stored as aggregate object { selections: [...] }; on /tasks as array of IDs
        if (currentUnit && typeof currentUnit === 'object' && !_isArray(currentUnit) && Array.isArray(currentUnit.selections)) {
          const filtered = currentUnit.selections.filter(
            (t: any) => (t?.value ?? t?.unitId ?? t?.id) != unitIdToRemove,
          );
          temp[TaskSearchFilterName.unit] =
            filtered.length === 0
              ? { ...initialUnitAggregateSearch }
              : { ...currentUnit, selections: filtered };
        } else {
          temp[TaskSearchFilterName.unit] =
            Array.isArray(currentUnit) ? currentUnit.filter(t => t != unitIdToRemove) : [];
        }
      } else {
        // Handle unit filter structure - it returns an object with selections property
        const unitValue = filter;
        const selectedUnits = unitValue?.selections 
          ? unitValue.selections.map(item => item.value || item.id)
          : Array.isArray(unitValue) 
            ? unitValue.map(item => item.value || item.id)
            : [];
        temp[TaskSearchFilterName.unit] = selectedUnits.filter(unit => unit !== undefined && unit !== null);
      }
      break;
    case TaskSearchFilterName.dueDate:
      if (remove) {
        temp.dueDateStart = null;
        temp.dueDateEnd = null;
      } else {
        temp.dueDateStart = moment(filter?.startDate).format();
        temp.dueDateEnd = moment(filter?.endDate).format();
      }
      break;
    default:
      if (remove && temp[filterName]) {
        temp[filterName] = temp[filterName]?.filter(t => t !== formattedFilterData?.value) || [];
      } else if (temp[filterName]) {
        temp[filterName] = formattedFilterData;
      }
      break;
  }
  return temp;
};

/** Optional map of field name -> chip label prefix (e.g. { createdByIds: 'From' } for "From: John Doe"). */
export const generateTaskChips = (values, disableDelete, selectedChips?, labelPrefixMap?: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(values)
      // Hide internal fields like sentDateMin/sentDateMax, followUpDateMin/followUpDateMax,
      // and boolean flags (isFollowUp, isOptionApproval, isFlagged, isCoverage) from chip rendering.
      .filter(
        ([key]) =>
          key !== 'sentDateMin' &&
          key !== 'sentDateMax' &&
          key !== 'followUpDateMin' &&
          key !== 'followUpDateMax' &&
          key !== 'isFollowUp' &&
          key !== 'isOptionApproval' &&
          key !== 'isFlagged' &&
          key !== 'isCoverage',
      )
      .map(field => [
        field[0],
        generateFieldChips(
          field[1],
          selectedChips ? selectedChips[field[0]] : undefined,
          labelPrefixMap?.[field[0]] ?? (field[0] === 'dueDate' ? 'Due Date' : field[0] === 'orderIds' ? 'OID' : undefined),
          false,
          null,
          disableDelete,
        ),
      ]),
  );

const coerceBool = (v: any): boolean | undefined => {
  if (v === true || v === false) return v;
  if (v === 'true' || v === 'True') return true;
  if (v === 'false' || v === 'False') return false;
  return undefined;
};

/**
 * Yes/No rows use `value: true | false`. Chips may nest choices under repeated `.value` / `.Value`
 * or use string booleans from serialized selects.
 */
const yesNoChoice = (opt: any): boolean | undefined => {
  if (opt == null || typeof opt !== 'object') return undefined;
  let cur: any = opt;
  for (let depth = 0; depth < 8; depth++) {
    const b = coerceBool(cur?.value ?? cur?.Value);
    if (b !== undefined) return b;
    const next = cur?.value ?? cur?.Value;
    if (next != null && typeof next === 'object') {
      cur = next;
      continue;
    }
    return coerceBool(next);
  }
  return undefined;
};

const yesNoSelectionsHave = (selections: any[], want: boolean): boolean =>
  Array.isArray(selections) && selections.some(opt => yesNoChoice(opt) === want);

export const formatFilterValues = values => {
  const formValues = { ...values };
  if (values?.dueDate) {
    const startDateValue = new Date(values?.dueDate?.startDate?.setHours(18));
    const endDatevalue = new Date(values?.dueDate?.endDate?.setHours(13));
    formValues.dueDate = {
      startDate: startDateValue,
      endDate: endDatevalue,
    };
  }
  const formatDateTimeForApi = (value: any): string | undefined => {
    if (!value) return undefined;
    const m = moment(value);
    if (!m.isValid()) return undefined;
    return m.format('YYYY-MM-DD[T]HH:mm:ss');
  };
  if (values?.sentDateRange) {
    const { startDate, endDate } = values.sentDateRange;
    const min = formatDateTimeForApi(moment(startDate).startOf('day'));
    const max = formatDateTimeForApi(moment(endDate).endOf('day').milliseconds(0));
    if (min) {
      formValues.sentDateMin = min;
    }
    if (max) {
      formValues.sentDateMax = max;
    }
  }
  if (values?.followUpDateRange) {
    const { startDate, endDate } = values.followUpDateRange;
    const min = formatDateTimeForApi(moment(startDate).startOf('day'));
    const max = formatDateTimeForApi(moment(endDate).endOf('day').milliseconds(0));
    if (min) {
      formValues.followUpDateMin = min;
    }
    if (max) {
      formValues.followUpDateMax = max;
    }
  }
  // For Global Search Tasks: map Follow-Up Yes/No multi-select to isFollowUp boolean for API
  // - If both Yes and No selected, or none selected -> do not send isFollowUp
  // - If only Yes selected -> isFollowUp = true
  // - If only No selected -> isFollowUp = false
  if (Array.isArray(values?.followUpFilter)) {
    const followUpSelections = values.followUpFilter;
    const hasYes = yesNoSelectionsHave(followUpSelections, true);
    const hasNo = yesNoSelectionsHave(followUpSelections, false);

    if ((hasYes && hasNo) || (!hasYes && !hasNo)) {
      delete formValues.isFollowUp;
    } else if (hasYes) {
      formValues.isFollowUp = true;
    } else if (hasNo) {
      formValues.isFollowUp = false;
    }
  }
  // For Global Search Tasks: map Option Approval Yes/No multi-select to isOptionApproval boolean for API
  // - If both Yes and No selected, or none selected -> do not send isOptionApproval
  // - If only Yes selected -> isOptionApproval = true
  // - If only No selected -> isOptionApproval = false
  if (Array.isArray(values?.optionApprovalFilter)) {
    const optionSelections = values.optionApprovalFilter;
    const hasYes = yesNoSelectionsHave(optionSelections, true);
    const hasNo = yesNoSelectionsHave(optionSelections, false);

    if ((hasYes && hasNo) || (!hasYes && !hasNo)) {
      delete formValues.isOptionApproval;
    } else if (hasYes) {
      formValues.isOptionApproval = true;
    } else if (hasNo) {
      formValues.isOptionApproval = false;
    }
  }
  // For Global Search Tasks: map Flagged Yes/No multi-select to isFlagged boolean for API
  // - If both Yes and No selected, or none selected -> do not send isFlagged
  // - If only Yes selected -> isFlagged = true
  // - If only No selected -> isFlagged = false
  if (Array.isArray(values?.flaggedFilter)) {
    const flaggedSelections = values.flaggedFilter;
    const hasYes = yesNoSelectionsHave(flaggedSelections, true);
    const hasNo = yesNoSelectionsHave(flaggedSelections, false);

    if ((hasYes && hasNo) || (!hasYes && !hasNo)) {
      delete formValues.isFlagged;
    } else if (hasYes) {
      formValues.isFlagged = true;
    } else if (hasNo) {
      formValues.isFlagged = false;
    }
  }
  // For Global Search Tasks: map Coverage Yes/No multi-select to isCoverage boolean for API
  // - If both Yes and No selected, or none selected -> do not send isCoverage
  // - If only Yes selected -> isCoverage = true
  // - If only No selected -> isCoverage = false
  if (Array.isArray(values?.coverageFilter)) {
    const coverageSelections = values.coverageFilter;
    const hasYes = yesNoSelectionsHave(coverageSelections, true);
    const hasNo = yesNoSelectionsHave(coverageSelections, false);

    if ((hasYes && hasNo) || (!hasYes && !hasNo)) {
      delete formValues.isCoverage;
    } else if (hasYes) {
      formValues.isCoverage = true;
    } else if (hasNo) {
      formValues.isCoverage = false;
    }
  }
  return formValues;
};

interface KeyValue {
  [key: string]: any;
}

export const convertArrayToKeyValuePairs = (arr: [string, any][]) => {
  const result: KeyValue = {};
  for (const [key, value] of arr) {
    switch (key) {
      case 'assignedToIds':
      case 'recipientIds':
      case 'taskStatusIds':
        result[key] = value.map(item =>
          key === 'assignedToIds' || key === 'recipientIds'
            ? {
                id: item.id,
                value: item.value,
                name: item.name,
              }
            : {
                id: item.id,
                name: item.name,
                value: item.value,
              },
        );
        break;
      case 'dueDate':
        result[key] = new Date(value?.startDate)?.toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
        });
        result[key] = new Date(value?.endDate)?.toLocaleString('en-US', {
          timeZone: 'America/Los_Angeles',
        });
      default:
        result[key] = value;
        break;
    }
  }
  return result;
};

export const convertSelectedFilterValue = (arr: [string, any][]) => {
  const result: KeyValue = {};
  const formattedDate: any = { startDate: null, endDate: null };

  for (const [key, value] of arr) {
    if (key === 'dueDate') {
      const dateValue = value;
      if (dateValue) {
        const startDateValue = moment.tz(dateValue?.startDate, 'America/Los_Angeles').format();
        const endDateValue = moment.tz(dateValue?.endDate, 'America/Los_Angeles').format();
        formattedDate.startDate = startDateValue;
        formattedDate.endDate = endDateValue;
      }
    }
    switch (key) {
      case 'assignedToIds':
      case 'recipientIds':
      case 'taskCategories':
      case 'taskStatusIds':
      case 'candidateIds':
      case 'placementIds':
      case 'orderIds':
      case 'facility':
      case 'facilityIds':
      case 'unit':
      case 'createdByIds':
        if (key === 'unit' && value && typeof value === 'object' && 'selections' in value) {
          result[key] = value.selections.map(item => item.value || item.id);
        } else {
          result[key] = value.map(item => item.value);
        }
        break;
      case 'candidateFilter':
        if (value && typeof value === 'object' && 'selections' in value) {
          result['candidateIds'] = value.selections.map(item => item.value || item.id || item.candidateId);
        } else if (Array.isArray(value)) {
          result['candidateIds'] = value.map(item => item.value || item.id || item.candidateId);
        }
        break;
      case 'dueDate':
        result[key] = formattedDate;
        break;
      default:
        result[key] = value;
        break;
    }
  }
  result.dueDateStart = formattedDate?.startDate;
  result.dueDateEnd = formattedDate?.endDate;
  return result;
};

export const setFormValues = (formData, setValue, DefaultDateValue?) => {
  setValue('assignedToIds', formData?.assignedToIds);
  setValue('taskCategories', formData?.taskCategories);
  setValue('taskStatusIds', formData?.taskStatusIds);
  setValue('dueDate', DefaultDateValue);
  setValue('candidateFilter', formData?.candidateFilter ?? initialCandidateAggregateSearch);
  setValue('candidateIds', formData?.candidateIds);
  setValue('placementIds', formData?.placementIds);
  setValue('orderIds', formData?.orderIds);
  setValue('facility', formData?.facility);
  setValue('facilityIds', formData?.facilityIds);
  setValue('unit', formData?.unit ?? initialUnitAggregateSearch);
  setValue('createdByIds', formData?.createdByIds);
  setValue('recipientIds', formData?.recipientIds);
};

export const formatDateValue = selectedFilters => {
  const startDateValue = new Date(selectedFilters?.dueDate?.startDate);
  const endDateValue = new Date(selectedFilters?.dueDate?.endDate);
  const formattedDateValue = { startDate: startDateValue, endDate: endDateValue };
  return formattedDateValue;
};

export const formatSelectedFilter = (selectedFilter: any) => {
  const dueDate = selectedFilter?.find(([key]) => key === 'dueDate');
  const dueDateIndex = selectedFilter?.findIndex(([key]) => key === 'dueDate');
  if (dueDate) {
    const startDateValue = dueDate[1] ? new Date(dueDate[1]?.startDate)?.toLocaleString() : new Date().toLocaleString();
    const endDateValue = dueDate[1] ? new Date(dueDate[1]?.endDate)?.toLocaleString() : new Date().toLocaleString();
    const formatedDateValue = { startDate: startDateValue, endDate: endDateValue };
    const newDueDateArray = ['dueDate', dueDate[1] ? formatedDateValue : null];
    const formatedFilterData = [...selectedFilter];
    formatedFilterData[dueDateIndex] = newDueDateArray;
    return formatedFilterData;
  }
  return selectedFilter;
};

export const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const checkDuplicateFilters = (obj1, obj2) => {
  const formattedObj1 = {
    ...obj1,
    dueDate: obj1?.dueDate
      ? {
          startDate: new Date(obj1.dueDate?.startDate),
          endDate: new Date(obj1.dueDate?.endDate),
        }
      : null,
  };
  const formattedObj2 = {
    ...obj2,
    dueDate: obj2?.dueDate
      ? {
          startDate: obj2.dueDate?.startDate,
          endDate: obj2.dueDate?.endDate,
        }
      : null,
  };
  if (formattedObj1.dueDate) {
    formattedObj1.dueDate.startDate = formatDate(formattedObj1.dueDate?.startDate);
    formattedObj1.dueDate.endDate = formatDate(formattedObj1.dueDate?.endDate);
  }

  if (formattedObj2.dueDate) {
    formattedObj2.dueDate.startDate = formatDate(obj2.dueDate?.startDate);
    formattedObj2.dueDate.endDate = formatDate(obj2.dueDate?.endDate);
  }

  // Check if objects are the same
  const checkSameObjects = _.isEqual(formattedObj1, formattedObj2);
  return checkSameObjects;
};
